import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../components/Table";
import { Dropdown } from "react-bootstrap";
import { usePersistStore, useStore } from "../../store";
import { DisputeService } from "../../services";
import { useMyDisputes } from "../../hooks/queries";
import Confirm from "../../components/Confirm";
import DisputeDetailsPopup from "../../components/DisputeDetailsPopup";
import DisputeStatusDropdown from "../../components/DisputeStatusDropdown";
import TableSearchInput from "../../components/TableSearchInput";
import { Utils } from "../../utils";
import { DisputeStatusChip } from "../../components/status";

function Disputes() {
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { token } = usePersistStore((s: any) => s);
  const { setIsLoading, setErrorMessage, setSuccessMessage } = useStore(
    (s: any) => s
  );

  const { data, isLoading, error, refetch } = useMyDisputes(token, {
    keyword: appliedKeyword,
    page,
    limit: rowsPerPage,
    status,
  });
  const list = data?.list || [];
  const count = data?.count || 0;
  const isDone = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [selectedObj, setSelectedObj] = useState<any>(null);

  const renderBadge = useCallback(
    (status: string) => <DisputeStatusChip status={status} />,
    []
  );

  const renderActions = useCallback(
    (row: any) => {
      return (
        <div className="profileDropdown">
          <Dropdown>
            <Dropdown.Toggle aria-label="Row actions">
              <i className="bi bi-three-dots-vertical" aria-hidden="true" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  setSelectedObj(row);
                  setShowDeleteConfirm(true);
                }}
              >
                <div className="d-flex gap-2 align-items-center">
                  <i className="bi bi-trash" aria-hidden="true" />
                  <span>Delete</span>
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      );
    },
    []
  );

  const renderMobileCard = useCallback(
    (row: any) => (
      <div className="bidsCard">
        <div className="bidsCard__top">
          <div>
            <span
              className="bidsCard__title"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedObj(row);
                setShowDetailsPopup(true);
              }}
            >
              {row?.subject}
            </span>
            {row?.code && (
              <div className="refCode refCode--sub">{row.code}</div>
            )}
          </div>
          <div className="bidsCard__status">{renderBadge(row?.status)}</div>
        </div>
        <div className="bidsCard__meta">
          <div className="bidsCard__metaRow">
            <i className="bi bi-image" aria-hidden="true"></i>
            <span>
              Artwork:{" "}
              <Link
                to={`/details/${row?.adId?._id}`}
                style={{ color: "var(--main-color)", textDecoration: "none" }}
              >
                {row?.adId?.title}
              </Link>
            </span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-person" aria-hidden="true"></i>
            <span>
              Seller:{" "}
              <Link
                to={`/seller/${row?.userId?._id}`}
                style={{ color: "var(--main-color)", textDecoration: "none" }}
              >
                {row?.userId?.name}
              </Link>
            </span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-calendar-event" aria-hidden="true"></i>
            <span>Created {Utils.formatDateTime(row?.createdAt)}</span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-arrow-repeat" aria-hidden="true"></i>
            <span>Updated {Utils.formatDateTime(row?.updatedAt)}</span>
          </div>
        </div>
        {renderActions(row) && (
          <div className="bidsCard__actions">{renderActions(row)}</div>
        )}
      </div>
    ),
    [renderBadge, renderActions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Code",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) =>
          row?.code ? (
            <span className="refCode">{row.code}</span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>—</span>
          ),
      },
      {
        title: "Subject",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <span
            style={{ color: "var(--main-color)", cursor: "pointer" }}
            onClick={() => {
              setSelectedObj(row);
              setShowDetailsPopup(true);
            }}
          >
            {row?.subject}
          </span>
        ),
      },
      {
        title: "Artwork",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link to={`/details/${row?.adId?._id}`} style={{ color: "var(--main-color)", textDecoration: "none" }}>
            {row?.adId?.title}
          </Link>
        ),
      },
      {
        title: "Seller",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link to={`/seller/${row?.userId?._id}`} style={{ color: "var(--main-color)", textDecoration: "none" }}>
            {row?.userId?.name}
          </Link>
        ),
      },
      {
        title: "Created at",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.createdAt),
      },
      {
        title: "Status update",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.updatedAt),
      },
      {
        title: "Status",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderBadge(row?.status),
      },
      {
        title: "Actions",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderActions(row),
      },
    ],
    [renderBadge, renderActions]
  );

  const handleDelete = useCallback(async () => {
    if (!selectedObj || !token) return;
    setIsLoading(true);
    let result = await DisputeService.deleteById(selectedObj?._id, token);
    if (result.error) {
      setErrorMessage(result.error);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setSelectedObj(null);
    setSuccessMessage("Your dispute is deleted successfully.");
    setShowDeleteConfirm(false);
    refetch();
  }, [
    selectedObj,
    token,
    setErrorMessage,
    setIsLoading,
    setSuccessMessage,
    refetch,
    setShowDeleteConfirm,
  ]);

  return (
    <>
      <Confirm
        show={showDeleteConfirm}
        onNo={() => {
          setSelectedObj(null);
          setShowDeleteConfirm(false);
        }}
        onYes={handleDelete}
        title="Are you sure?"
        body="Are you sure you want to delete this artwork? This action is irreversible."
      />
      <DisputeDetailsPopup
        show={showDetailsPopup}
        onHide={() => {
          setSelectedObj(null);
          setShowDetailsPopup(false);
        }}
        data={selectedObj}
      />
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4">
              <div className="tableCardHeader px-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">My Disputes</h2>
                  <p className="tableCardHeader__subtitle m-0">
                    {isDone
                      ? `${count} ${count === 1 ? "dispute" : "disputes"} total`
                      : "Loading your disputes…"}
                  </p>
                </div>
                <div className="tableCardHeader__actions customTitle">
                  <DisputeStatusDropdown value={status} onChange={setStatus} />
                  <TableSearchInput
                    value={keyword}
                    onChange={setKeyword}
                    onEnter={() => setAppliedKeyword(keyword)}
                    placeholder="Search subject"
                  />
                </div>
              </div>
              <Table
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={page}
                setPage={setPage}
                columns={columns}
                rows={list}
                isDone={isDone}
                count={count}
                mobileCard={renderMobileCard}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Disputes;
